import { forwardRef, type JSX, type MouseEvent, useCallback, useRef } from 'react';
import styled from 'styled-components';

import { CloseSVG } from '../svgs';
import ButtonLoader from './button-loader';

export type btnType = 'outlined' | 'bold' | 'disabled' | 'danger' | 'active' | 'refresh' | 'success' | 'ghost' | undefined;

export type btnSize = 'little' | 'small' | 'medium' | 'large' | 'table' | undefined;

interface IButton extends React.ComponentPropsWithoutRef<'button'> {
	buttonType?: btnType;
	buttonSize?: btnSize;
	title: string;
	load?: boolean;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	close?: boolean;
	status?: boolean;
	icon?: string | JSX.Element;
	iconPosition?: 'left' | 'right';
	fullWidth?: boolean;
	enableRipple?: boolean;
}

export const TypeButton = forwardRef<HTMLButtonElement, IButton>(
	(
		{
			buttonType,
			buttonSize,
			title,
			load,
			onClick,
			close,
			icon,
			iconPosition = 'right',
			disabled,
			fullWidth,
			enableRipple = true,
			...props
		},
		ref
	) => {
		const isIconSTR = typeof icon === 'string';
		const isLoadDark = buttonType === 'outlined' || buttonType === 'refresh' || buttonType === 'ghost';
		const btnTypeClass: btnType = disabled ? 'disabled' : buttonType;
		const rippleRef = useRef<HTMLSpanElement>(null);

		const createRipple = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				if (!enableRipple || disabled || load) return;

				const button = event.currentTarget;
				const ripple = document.createElement('span');
				const rect = button.getBoundingClientRect();
				const size = Math.max(rect.width, rect.height);
				const x = event.clientX - rect.left - size / 2;
				const y = event.clientY - rect.top - size / 2;

				ripple.style.cssText = `
				position: absolute;
				width: ${size}px;
				height: ${size}px;
				left: ${x}px;
				top: ${y}px;
				background: currentColor;
				opacity: 0.3;
				border-radius: 50%;
				transform: scale(0);
				animation: ripple 0.6s ease-out;
				pointer-events: none;
			`;

				button.appendChild(ripple);

				ripple.addEventListener('animationend', () => {
					ripple.remove();
				});
			},
			[enableRipple, disabled, load]
		);

		const handleClick = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				createRipple(event);
				if (!load && onClick) {
					onClick(event);
				}
			},
			[createRipple, load, onClick]
		);

		const renderContent = () => {
			if (load) {
				return <ButtonLoader className={isLoadDark ? 'bg-dark' : ''} />;
			}

			if (close) {
				return <CloseSVG />;
			}

			const iconElement = isIconSTR ? <i className={icon} /> : icon;

			return (
				<span className='btn-content'>
					{icon && iconPosition === 'left' && <span className='btn-icon'>{iconElement}</span>}
					<span className='btn-text'>{title}</span>
					{icon && iconPosition === 'right' && <span className='btn-icon'>{iconElement}</span>}
				</span>
			);
		};

		return (
			<TypeButtonComponent
				className={props.className || ''}
				$fullWidth={fullWidth}
			>
				<button
					{...props}
					className={`${btnTypeClass || ''} ${buttonSize || ''} ${props.className || ''}`}
					ref={ref}
					onClick={handleClick}
					disabled={disabled || load}
					aria-busy={load}
					// `buttonType: 'disabled'` is a skin that keeps its handler (so it can explain what
					// is missing) — announce it as disabled without removing the click.
					aria-disabled={disabled || btnTypeClass === 'disabled' || undefined}
				>
					<span
						ref={rippleRef}
						className='ripple-container'
					/>
					{renderContent()}
				</button>
			</TypeButtonComponent>
		);
	}
);

TypeButton.displayName = 'TypeButton';

export const TypeButtonComponent = styled.div<{ $fullWidth?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	height: max-content;
	width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'max-content')};

	@keyframes ripple {
		0% {
			transform: scale(0);
			opacity: 0.6;
		}
		100% {
			transform: scale(4);
			opacity: 0;
		}
	}

	button {
		position: relative;
		display: inline-flex;
		padding: 1rem 1.75rem;
		justify-content: center;
		align-items: center;
		gap: 0.625rem;
		border-radius: var(--radius-sm, 0.2625rem);
		background: var(--btn-primary-bg, #0f1a2e);
		outline: none;
		color: var(--btn-primary-text, #fff);
		font-size: 1rem;
		font-style: normal;
		font-weight: 700;
		line-height: normal;
		border: 1.5px solid var(--btn-primary-border, #0f1a2e);
		cursor: pointer;
		overflow: hidden;
		width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
		transition:
			background-color var(--transition-fast, 100ms) var(--transition-timing, ease),
			border-color var(--transition-fast, 100ms) var(--transition-timing, ease),
			transform var(--transition-fast, 100ms) var(--transition-timing, ease),
			box-shadow var(--transition-fast, 100ms) var(--transition-timing, ease);

		.btn-content {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			position: relative;
			z-index: 1;
		}

		.btn-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			transition: transform var(--transition-fast, 100ms) ease;
		}

		.ripple-container {
			position: absolute;
			inset: 0;
			pointer-events: none;
		}

		/* Hover states — never on the 'disabled' skin, which has no [disabled] attribute */
		&:hover:not(:disabled):not(.disabled) {
			background: var(--btn-primary-hover-bg, #264d66);
			transform: translateY(-1px);
			box-shadow: var(--shadow-sm, 0 2px 4px rgba(0, 0, 0, 0.1));
		}

		&:active:not(:disabled):not(.disabled) {
			transform: translateY(0) scale(0.98);
			box-shadow: none;
		}

		&:focus-visible {
			outline: 2px solid var(--color-primary, #1b6b4a);
			outline-offset: 2px;
		}

		/* Disabled state */
		&.disabled,
		&:disabled {
			background: var(--btn-disabled-bg, #eaeaea);
			cursor: not-allowed;
			color: var(--btn-disabled-text, #9a9a9a);
			border-color: var(--btn-disabled-border, #eaeaea);
			transform: none;
			box-shadow: none;
		}

		/* Outlined variant */
		&.outlined {
			background: var(--btn-outlined-bg, transparent);
			color: var(--btn-outlined-text, #0f1a2e);
			border-color: var(--btn-outlined-border, #0f1a2e);

			&:hover:not(:disabled) {
				background: rgba(15, 26, 46, 0.05);
			}

			&:active:not(:disabled) {
				background: rgba(15, 26, 46, 0.1);
			}
		}

		/* Danger variant */
		&.danger {
			color: var(--btn-danger-text, #e74c3c);
			background: var(--btn-danger-bg, #fffafc);
			border-color: var(--btn-danger-border, #ffe1ec);

			&:hover:not(:disabled) {
				background: #fff0f4;
				border-color: #f56e9d;
			}

			&:active:not(:disabled) {
				background: #ffe6ed;
			}
		}

		/* Success variant */
		&.success {
			color: #ffffff;
			background: var(--color-success, #4caf50);
			border-color: var(--color-success, #4caf50);

			&:hover:not(:disabled) {
				background: #43a047;
				border-color: #43a047;
			}

			&:active:not(:disabled) {
				background: #388e3c;
			}
		}

		/* Ghost variant */
		&.ghost {
			color: var(--text-secondary, #616161);
			background: transparent;
			border-color: transparent;

			&:hover:not(:disabled) {
				background: var(--bg-hover, #f5f5f5);
			}

			&:active:not(:disabled) {
				background: var(--bg-tertiary, #eeeeee);
			}
		}

		/* Refresh variant */
		&.refresh {
			color: var(--text-secondary, #7f8c8d);
			background: var(--bg-secondary, #f7f7f7);
			border-color: var(--bg-secondary, #f7f7f7);

			&:hover:not(:disabled) {
				background: var(--bg-hover, #f0f0f0);
			}
		}

		/* Size variants */
		&.little {
			font-size: 0.625rem;
			font-weight: 600;
			line-height: normal;
			display: inline-flex;
			padding: 0.52rem 0.79rem;
			width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'max-content')};
		}

		&.small {
			font-size: 0.72rem;
			font-weight: 700;
			line-height: normal;
			display: inline-flex;
			padding: 0.75rem 1.19rem;
			width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'max-content')};
		}

		&.medium {
			font-size: 0.8rem;
			font-weight: 700;
			line-height: normal;
			display: inline-flex;
			padding: 0.79rem 1.19rem;
			width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'max-content')};
			height: auto;
		}

		&.large {
			font-size: 1rem;
			font-weight: 700;
			padding: 1rem 2rem;
			width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'max-content')};
		}

		/* Icon hover animation */
		&:hover:not(:disabled) .btn-icon {
			transform: translateX(2px);
		}

		/* Loading state - pulse animation */
		&[aria-busy='true'] {
			opacity: 0.85;
			pointer-events: none;
		}
	}
`;
